import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { QBO_OAUTH_STATE_KEY, qboRedirectUri } from '@/features/integrations/qboOAuth';

/**
 * QuickBooks Online OAuth2 redirect handler. Intuit redirects here with
 * `code` + `realmId` + `state`. We:
 *   1. Verify `state` against the value stashed in sessionStorage by
 *      IntegrationsPage (CSRF protection).
 *   2. Forward `code`/`realmId`/`redirect_uri` to the qbo-oauth-callback
 *      Edge Function which exchanges the code for a refresh token and
 *      persists it in Supabase Vault.
 *   3. Kick off an immediate `qbo-sync` so the user sees Chart of Accounts
 *      + P&L data populate by the time they're back on /integrations.
 */
export function QboCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode mounts effects twice in dev; the OAuth `code` is
  // single-use, so guard against the second invocation eating the error.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const realmId = params.get('realmId');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setError(`QuickBooks rejected the connection: ${oauthError}`);
      return;
    }
    if (!code || !realmId) {
      setError('Missing OAuth parameters from QuickBooks.');
      return;
    }

    const expectedState = sessionStorage.getItem(QBO_OAUTH_STATE_KEY);
    if (!expectedState || !state || state !== expectedState) {
      setError(
        'OAuth state mismatch. For your security, please retry the connection from the Integrations page.',
      );
      return;
    }
    sessionStorage.removeItem(QBO_OAUTH_STATE_KEY);

    const redirectUri = qboRedirectUri();

    (async () => {
      const { error: fnError } = await supabase.functions.invoke('qbo-oauth-callback', {
        body: { code, realmId, state, redirect_uri: redirectUri },
      });
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setError(msg);
        return;
      }

      toast.success('QuickBooks connected. Pulling chart of accounts…');

      // Best-effort initial sync. Failures here are non-fatal — the 15-minute
      // cron job will retry, and the integration is already marked connected.
      const { error: syncError } = await supabase.functions.invoke('qbo-sync', { body: {} });
      if (syncError) {
        const msg = await extractFunctionErrorMessage(syncError);
        toast.error(`Connected, but initial sync failed: ${msg}`);
      } else {
        toast.success('QuickBooks sync complete.');
      }

      navigate('/integrations', { replace: true });
    })();
  }, [params, navigate]);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-3 text-center">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
